import React, { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { etaShort } from "@veyso/core";
import { JOB_ACTIVITY_LABELS, useJobActivity, type ActiveJob } from "../lib/jobActivity.js";
import { chipText, jobHref, menuNote } from "../lib/jobsMenu.js";
import { useEta, useEtas } from "../lib/useEta.js";

/**
 * What the app is doing, in the bar, on every screen: one chip, and a menu of
 * everything being made with the time left on each.
 *
 * Work happens in a worker and screens read the database afterwards, so until
 * now the only sign that anything was under way was that the page you were
 * looking at was quietly out of date — which is indistinguishable from nothing
 * happening, and the fix people find is refreshing the whole page.
 *
 * Deliberately not a modal. This runs for seconds to minutes and blocking the
 * editor behind it would stop you doing the one thing worth doing while you
 * wait, which is reading what it produced last time.
 */

/**
 * Never the raw type (same words as `jobWords` in lib/jobsMenu).
 *
 * The fallback used to be `first.type`, so a job kind nobody had written a
 * label for put its enum value in the nav bar — which is how "thumbnail"
 * came to blink at people every time they typed in the editor. The word for
 * a thing we have not described is "Working", not its column value.
 */
const words = (job: ActiveJob) => job.label ?? JOB_ACTIVITY_LABELS[job.type] ?? "Working";

const JobRow: React.FC<{ job: ActiveJob; onPick: () => void }> = ({ job, onPick }) => {
  const left = etaShort(useEta(job.id, job.eta));
  const percent = Math.round(Math.min(1, Math.max(0, job.progress ?? 0)) * 100);
  return (
    <li>
      <Link
        to={jobHref(job)}
        onClick={onPick}
        className="flex flex-col gap-1.5 rounded-[8px] p-3 text-[13px] hover:bg-bay-800"
      >
        <span className="flex items-baseline justify-between gap-3">
          <span className="truncate font-medium text-ink-100">{job.projectName}</span>
          {left ? <span className="tnum shrink-0 text-ink-400">{left}</span> : null}
        </span>
        <span className="truncate text-ink-300">{words(job)}</span>
        <span className="h-[5px] overflow-hidden rounded-full bg-bay-700" aria-hidden>
          <span className="block h-full rounded-full bg-ember-400" style={{ width: `${percent}%` }} />
        </span>
      </Link>
    </li>
  );
};

export const JobStatus: React.FC = () => {
  const { active } = useJobActivity();
  const [open, setOpen] = useState(false);
  // Smoothed under the same job ids as the rows, so the chip and the menu
  // never disagree about the same job.
  const shown = useEtas(active.map((job) => ({ key: job.id, eta: job.eta })));
  // Wraps the chip as well as the menu, so a press on the chip is the chip's
  // own to handle: otherwise it closes the menu and the click reopens it.
  const box = useRef<HTMLDivElement>(null);
  const chip = useRef<HTMLButtonElement>(null);
  const panel = useId();
  const showing = open && active.length > 0;
  // Closed when the last job ends, so the next one to start does not pop the
  // menu open by itself. Set while rendering, as React suggests for state
  // that follows a prop.
  if (open && active.length === 0) setOpen(false);

  useEffect(() => {
    if (!showing) return;
    // Escape hands focus back to the chip, so a keyboard user is not left nowhere.
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      chip.current?.focus();
    };
    const onDown = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [showing]);

  if (active.length === 0) return null;
  const text = chipText(active.map((job, i) => ({ label: words(job), eta: shown[i] })));
  const note = menuNote(active);

  return (
    <div ref={box} className="relative">
      <button
        ref={chip}
        type="button"
        aria-expanded={showing}
        aria-controls={panel}
        onClick={() => setOpen(!open)}
        className="flex h-[30px] max-w-full items-center gap-2 rounded-full border border-[var(--edge)] bg-bay-850 px-3 text-[12px] text-ink-200"
      >
        <span className="h-[7px] w-[7px] shrink-0 animate-pulse rounded-full bg-ember-400" aria-hidden />
        <span className="truncate">{text}</span>
      </button>
      {showing ? (
        <div
          id={panel}
          className="absolute right-0 top-[calc(100%+8px)] z-40 flex w-[420px] max-w-[calc(100vw-32px)] flex-col rounded-[12px] border border-[var(--edge-strong)] bg-bay-850 p-2 shadow-xl"
        >
          <ul className="m-0 flex list-none flex-col p-0">
            {active.map((job) => (
              <JobRow key={job.id} job={job} onPick={() => setOpen(false)} />
            ))}
          </ul>
          {note ? (
            <p className="mt-1 border-t border-[var(--edge)] p-3 text-[13px] leading-relaxed text-ink-300">{note}</p>
          ) : null}
        </div>
      ) : null}
      <span className="sr-only" role="status" aria-live="polite">
        {text}
      </span>
    </div>
  );
};
